import React, { createContext, useCallback, useContext, useMemo } from 'react'

import Link from 'next/link'
import {
  HiArrowTopRightOnSquare,
  HiMiniCheck,
  HiOutlineChatBubbleLeftRight,
  HiOutlineSquare3Stack3D,
} from 'react-icons/hi2'
import { TbPencilBolt } from 'react-icons/tb'
import { twMerge } from 'tailwind-merge'

import DemoTooltip from '@/components/billing/DemoTooltip'
import { useCreateOpportunityChatSession } from '@/components/chat-v2/hooks/useChatSession'
import Button from '@/components/common/Button'

import { useCanAccessBasic } from '@/hooks/billing/useCanAccessBasic'
import { useCanAccessPremium } from '@/hooks/billing/useCanAccessPremium'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import {
  type OpportunityInput,
  type UsePursuitCreationReturn,
  usePursuitCreation,
} from '@/hooks/opportunity/usePursuitCreation'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { UUID } from '@/types/Id'

// ---------------------------------------------------------------------------
// Context for compound component pattern
// ---------------------------------------------------------------------------

interface PursuitActionsContextValue extends UsePursuitCreationReturn {
  canAccessBasic: boolean
  canAccessPremium: boolean
  disabled: boolean
  // Ask AI state and actions
  askAi: (openInNewTab?: boolean) => Promise<void>
  isCreatingChatSession: boolean
  // Begin response with new tab option
  beginResponseNewTab: () => Promise<void>
}

const PursuitActionsContext = createContext<PursuitActionsContextValue | null>(
  null,
)

function usePursuitActionsContext() {
  const ctx = useContext(PursuitActionsContext)
  if (!ctx) {
    throw new Error(
      'PursuitActions components must be used within PursuitActions.Root',
    )
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Root component (Provider)
// ---------------------------------------------------------------------------

export interface PursuitActionsRootProps {
  /** The opportunity data to create a pursuit from */
  opportunity: OpportunityInput | null
  /** Optional search/campaign ID */
  searchId?: string | null
  /** Whether to enable the hook (default: true) */
  enabled?: boolean
  /** External disabled state (e.g., when org is not selected) */
  disabled?: boolean
  /** Children (button components) */
  children: React.ReactNode
  /** Additional className for the container */
  className?: string
}

function Root({
  opportunity,
  searchId,
  enabled = true,
  disabled = false,
  children,
  className,
}: PursuitActionsRootProps) {
  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()

  const pursuitState = usePursuitCreation({
    opportunity,
    searchId,
    enabled,
  })

  const { canAccessBasic } = useCanAccessBasic()
  const { canAccessPremium } = useCanAccessPremium()

  const {
    mutateAsync: createOpportunityChatSession,
    isPending: isCreatingChatSession,
  } = useCreateOpportunityChatSession()

  const askAi = useCallback(
    async (openInNewTab = false) => {
      if (!activeOrgId || !opportunity) return

      const session = await createOpportunityChatSession({
        opportunity_id: opportunity.entityId as UUID,
        opportunity_type: opportunity.type,
        organization_id: activeOrgId,
        title: opportunity.title ?? 'Opportunity Thread',
      })
      if (session) {
        const url = `/orgs/${activeOrgId}/c/opp/${session.id}`
        if (openInNewTab) {
          window.open(url, '_blank')
        } else {
          safePush(url)
        }
      }
    },
    [activeOrgId, opportunity, createOpportunityChatSession, safePush],
  )

  const beginResponseNewTab = useCallback(async () => {
    if (!opportunity || !activeOrgId) return

    const { pursuitId: existingId } = pursuitState

    if (existingId) {
      window.open(
        `/orgs/${activeOrgId}/pursuits/${existingId}/documents?newDocument=true`,
        '_blank',
      )
      return
    }

    // Need to create pursuit first - delegate to beginResponse but open result in new tab
    // For simplicity, just call the regular flow - user will need to use regular click
    // if pursuit doesn't exist yet
  }, [opportunity, activeOrgId, pursuitState])

  const contextValue = useMemo<PursuitActionsContextValue>(
    () => ({
      ...pursuitState,
      canAccessBasic: canAccessBasic ?? false,
      canAccessPremium: canAccessPremium ?? false,
      disabled,
      askAi,
      isCreatingChatSession,
      beginResponseNewTab,
    }),
    [
      pursuitState,
      canAccessBasic,
      canAccessPremium,
      disabled,
      askAi,
      isCreatingChatSession,
      beginResponseNewTab,
    ],
  )

  return (
    <PursuitActionsContext.Provider value={contextValue}>
      {children}
    </PursuitActionsContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// CreatePursuitButton component
// ---------------------------------------------------------------------------

export interface CreatePursuitButtonProps {
  /** Additional className for the button */
  className?: string
  /** Button variant when not yet pursued (default: 'outline') */
  variant?: 'outline' | 'primary' | 'primary-compact' | 'outline-compact'
  /** Button variant when pursuit exists (default: 'positive-compact') */
  successVariant?:
    | 'positive'
    | 'positive-compact'
    | 'green-compact'
    | 'teal-compact'
  /** Label text when not yet pursued (default: 'Create Pursuit') */
  label?: string
  /** Label text when creating (default: 'Creating...') */
  loadingLabel?: string
  /** Label text after pursuit created (default: 'Created Pursuit') */
  successLabel?: string
  /** Label text on hover after pursuit created (default: 'View Pursuit') */
  hoverLabel?: string
}

function CreatePursuitButton({
  className,
  variant = 'outline',
  successVariant = 'positive-compact',
  label = 'Create Pursuit',
  loadingLabel = 'Creating...',
  successLabel = 'Created Pursuit',
  hoverLabel = 'View Pursuit',
}: CreatePursuitButtonProps) {
  const {
    canCreate,
    canAccessBasic,
    disabled,
    isCreating,
    hasCreatedPursuit,
    createPursuit,
    pursuitUrl,
  } = usePursuitActionsContext()

  const isButtonDisabled = disabled || isCreating || !canAccessBasic

  // Show success link if pursuit exists (using Link for middle-click support)
  if (hasCreatedPursuit && pursuitUrl) {
    return (
      <Link href={pursuitUrl} className={twMerge('group', className)}>
        <Button
          variant={successVariant}
          className={twMerge('w-full', className)}
        >
          {/* Icon container - stacks both icons in same position */}
          <span className="relative h-4 w-4">
            <HiMiniCheck className="absolute inset-0 h-4 w-4 transition-opacity duration-150 group-hover:opacity-0" />
            <HiArrowTopRightOnSquare className="absolute inset-0 h-4 w-4 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
          </span>
          {/* Success label - visible by default, hidden on hover */}
          <span className="text-sm font-medium transition-opacity duration-150 group-hover:hidden">
            {successLabel}
          </span>
          {/* Hover label - hidden by default, visible on hover */}
          <span className="hidden text-sm font-medium transition-opacity duration-150 group-hover:inline">
            {hoverLabel}
          </span>
        </Button>
      </Link>
    )
  }

  // Show create button
  return (
    <DemoTooltip childrenOnly={canAccessBasic}>
      <Button
        variant={variant}
        onClick={createPursuit}
        disabled={isButtonDisabled || !canCreate}
        isLoading={isCreating}
        className={className}
      >
        <HiOutlineSquare3Stack3D className="h-4 w-4" />
        <span className="text-sm font-medium">
          {isCreating ? loadingLabel : label}
        </span>
      </Button>
    </DemoTooltip>
  )
}

// ---------------------------------------------------------------------------
// BeginResponseButton component
// ---------------------------------------------------------------------------

export interface BeginResponseButtonProps {
  className?: string
}

function BeginResponseButton({ className }: BeginResponseButtonProps) {
  const {
    canBeginResponse,
    canAccessPremium,
    disabled,
    isNavigating,
    isCreating,
    beginResponse,
    beginResponseNewTab,
    pursuitId,
  } = usePursuitActionsContext()

  const isButtonDisabled =
    disabled ||
    !canAccessPremium ||
    isNavigating ||
    isCreating ||
    !canBeginResponse

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Ctrl+click or Cmd+click opens in new tab (only if pursuit exists)
      if ((e.ctrlKey || e.metaKey) && pursuitId) {
        e.preventDefault()
        beginResponseNewTab()
      } else {
        beginResponse()
      }
    },
    [beginResponse, beginResponseNewTab, pursuitId],
  )

  const handleAuxClick = useCallback(
    (e: React.MouseEvent) => {
      // Middle-click opens in new tab (only if pursuit exists)
      if (e.button === 1 && pursuitId) {
        e.preventDefault()
        beginResponseNewTab()
      }
    },
    [beginResponseNewTab, pursuitId],
  )

  return (
    <DemoTooltip childrenOnly={canAccessPremium}>
      <Button
        variant="outline-compact"
        onClick={handleClick}
        onAuxClick={handleAuxClick}
        disabled={isButtonDisabled}
        isLoading={isNavigating}
        className={className}
      >
        <TbPencilBolt className="h-4 w-4" />
        Begin Response
      </Button>
    </DemoTooltip>
  )
}

// ---------------------------------------------------------------------------
// AskAiButton component
// ---------------------------------------------------------------------------

export interface AskAiButtonProps {
  className?: string
}

function AskAiButton({ className }: AskAiButtonProps) {
  const { canAccessBasic, disabled, isCreatingChatSession, askAi, isCreating } =
    usePursuitActionsContext()

  const isButtonDisabled =
    disabled || !canAccessBasic || isCreatingChatSession || isCreating

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Ctrl+click or Cmd+click opens in new tab
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        askAi(true)
      } else {
        askAi(false)
      }
    },
    [askAi],
  )

  const handleAuxClick = useCallback(
    (e: React.MouseEvent) => {
      // Middle-click opens in new tab
      if (e.button === 1) {
        e.preventDefault()
        askAi(true)
      }
    },
    [askAi],
  )

  return (
    <DemoTooltip childrenOnly={canAccessBasic}>
      <Button
        variant="primary-compact"
        onClick={handleClick}
        onAuxClick={handleAuxClick}
        disabled={isButtonDisabled}
        isLoading={isCreatingChatSession}
        className={className}
      >
        <HiOutlineChatBubbleLeftRight className="h-4 w-4" />
        Ask Sweetspot AI
      </Button>
    </DemoTooltip>
  )
}

// ---------------------------------------------------------------------------
// Headless hook consumer for custom UI
// ---------------------------------------------------------------------------

export interface PursuitActionsRenderProps extends UsePursuitCreationReturn {
  canAccessBasic: boolean
  canAccessPremium: boolean
  disabled: boolean
}

export interface CustomPursuitActionsProps {
  children: (props: PursuitActionsRenderProps) => React.ReactNode
}

/**
 * Render prop component for custom UI implementations.
 * Use when you need full control over the button rendering.
 *
 * @example
 * ```tsx
 * <PursuitActions.Root opportunity={opportunityInput}>
 *   <PursuitActions.Custom>
 *     {({ createPursuit, isCreating, canCreate }) => (
 *       <MyCustomButton
 *         onClick={createPursuit}
 *         loading={isCreating}
 *         disabled={!canCreate}
 *       />
 *     )}
 *   </PursuitActions.Custom>
 * </PursuitActions.Root>
 * ```
 */
function Custom({ children }: CustomPursuitActionsProps) {
  const ctx = usePursuitActionsContext()
  return <>{children(ctx)}</>
}

// ---------------------------------------------------------------------------
// Export compound component
// ---------------------------------------------------------------------------

export const PursuitActions = {
  Root,
  CreatePursuitButton,
  BeginResponseButton,
  AskAiButton,
  Custom,
}

// ---------------------------------------------------------------------------
// Convenience: Standalone hook re-export for non-compound usage
// ---------------------------------------------------------------------------

export {
  usePursuitCreation,
  type OpportunityInput,
  type UsePursuitCreationOptions,
  type UsePursuitCreationReturn,
  buildFederalContractInput,
  buildFederalGrantInput,
  buildSbirInput,
  buildSledContractInput,
} from '@/hooks/opportunity/usePursuitCreation'
